/**
 * platformCrmService — accesso dati del CRM di piattaforma (super utente).
 *
 * Pattern console: letture/scritture Firestore DIRETTE (la console è solo
 * super_admin, le security rules bloccano chiunque altro), come
 * TenantBillingTab / PricingPage. Unica eccezione: la generazione dei link
 * questionario passa dalla callable `createQuestionnaireLink` (il token va
 * generato server-side, crittograficamente sicuro).
 *
 * Doc canonico: docs/01-ARCHITECTURE/PLATFORM-CRM-QUESTIONARI-MODEL.md
 */

import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  indirizzoPaginaPubblica,
  normalizzaEmail,
  problemiDefinizione,
  RACCOLTA_SESSIONI_QUESTIONARI,
  SOTTORACCOLTA_REGISTRO_LEAD,
  type QuestionarioSalvato,
  type VoceStoricoConsenso,
} from '@shared/questionari';
import { auth, db, functions } from '../firebase';
import type {
  PlatformLead,
  PlatformLeadWriteInput,
  PlatformQuestionnaire,
  PlatformQuestionnaireLink,
  PlatformQuestionnaireResponse,
  PlatformQuestionnaireSessione,
  VoceRegistroLead,
} from '../types/platformCrm';

const LEADS_COLLECTION = 'platform_leads';
const QUESTIONNAIRES_COLLECTION = 'platform_questionnaires';
const LINKS_COLLECTION = 'platform_questionnaire_links';
const RESPONSES_COLLECTION = 'platform_questionnaire_responses';

// ============================================================================
// MAPPING — regola progetto: mai whitelist, sempre spread + override id
// ============================================================================

function mapSnap<T>(d: QueryDocumentSnapshot<DocumentData>): T {
  return { ...(d.data() as Record<string, unknown>), id: d.id } as T;
}

/** Millisecondi da un campo timestamp Firestore (0 se assente/pending). */
function tsMillis(value: unknown): number {
  return value instanceof Timestamp ? value.toMillis() : 0;
}

/** Rimuove le chiavi `undefined` (Firestore rifiuta i valori undefined). */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}

/**
 * Come `stripUndefined`, a ogni livello (oggetti dentro oggetti e dentro elenchi): la definizione
 * di un questionario è annidata, e un solo `undefined` in fondo fa rifiutare tutto il documento.
 * I valori speciali di Firestore (Timestamp, serverTimestamp) passano intatti.
 */
function senzaUndefined<T>(valore: T): T {
  if (Array.isArray(valore)) return valore.map((v) => senzaUndefined(v)) as unknown as T;
  if (valore && typeof valore === 'object' && Object.getPrototypeOf(valore) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(valore as Record<string, unknown>)) {
      if (v !== undefined) out[k] = senzaUndefined(v);
    }
    return out as T;
  }
  return valore;
}

// ============================================================================
// PARTITA IVA — normalizzazione + validazione formato (chiave di dedupe)
// ============================================================================

/** Normalizza una P.IVA: rimuove spazi/punti e l'eventuale prefisso IT. */
export function normalizePartitaIva(raw: string): string {
  return raw.replace(/[\s.]/g, '').replace(/^IT/i, '').trim();
}

/**
 * Valida il formato della partita IVA italiana: 11 cifre + cifra di controllo
 * (algoritmo standard: posizioni dispari sommate, pari raddoppiate con -9 se > 9,
 * totale divisibile per 10). Restituisce la forma normalizzata da salvare.
 */
export function validatePartitaIva(raw: string): { ok: boolean; normalized: string; error?: string } {
  const normalized = normalizePartitaIva(raw);
  if (!/^\d{11}$/.test(normalized)) {
    return { ok: false, normalized, error: 'la partita IVA deve avere 11 cifre (prefisso IT opzionale)' };
  }
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    let n = normalized.charCodeAt(i) - 48;
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  if (sum % 10 !== 0) {
    return { ok: false, normalized, error: 'partita IVA non valida (cifra di controllo errata)' };
  }
  return { ok: true, normalized };
}

// ============================================================================
// PLATFORM LEADS
// ============================================================================

/** Carica tutti i lead di piattaforma, ordinati per ultimo aggiornamento. */
export async function listPlatformLeads(): Promise<PlatformLead[]> {
  const snap = await getDocs(collection(db, LEADS_COLLECTION));
  const leads = snap.docs.map((d) => mapSnap<PlatformLead>(d));
  leads.sort(
    (a, b) =>
      (tsMillis(b.updated_at) || tsMillis(b.created_at)) -
      (tsMillis(a.updated_at) || tsMillis(a.created_at)),
  );
  return leads;
}

/**
 * Valori distinti di `partner_segnalatore` presenti nei lead caricati,
 * ordinati alfabeticamente (it). Alimenta autocomplete e filtri SENZA
 * hardcode: i partner sono valori nei dati, mai costanti nel codice.
 */
export function distinctPartnerSegnalatori(leads: PlatformLead[]): string[] {
  const values = new Set<string>();
  for (const lead of leads) {
    const value = (lead.partner_segnalatore ?? '').trim();
    if (value) values.add(value);
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
}

/** Cerca un lead per P.IVA (già normalizzata) — usato per la dedupe. */
export async function findLeadByPartitaIva(partitaIva: string): Promise<PlatformLead | null> {
  const q = query(
    collection(db, LEADS_COLLECTION),
    where('partita_iva', '==', partitaIva),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return mapSnap<PlatformLead>(snap.docs[0]);
}

/**
 * La chiave email accanto all'email: il server riconosce chi torna da un questionario senza
 * partita IVA proprio da `email_normalizzata`, quindi ogni scrittura dell'email la aggiorna.
 */
function conChiaveEmail<T extends { email?: string }>(input: T): T & { email_normalizzata?: string | null } {
  if (input.email === undefined) return input;
  return { ...input, email_normalizzata: normalizzaEmail(input.email) };
}

/** Crea un nuovo lead di piattaforma. Restituisce l'id del documento creato. */
export async function createPlatformLead(input: PlatformLeadWriteInput): Promise<string> {
  const ref = await addDoc(collection(db, LEADS_COLLECTION), stripUndefined({
    ...conChiaveEmail(input),
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    created_by_uid: auth.currentUser?.uid ?? null,
  }));
  return ref.id;
}

/** Aggiorna i campi editabili di un lead (updateDoc diretto, pattern console). */
export async function updatePlatformLead(
  id: string,
  patch: Partial<PlatformLeadWriteInput>,
): Promise<void> {
  await updateDoc(doc(db, LEADS_COLLECTION, id), stripUndefined({
    ...conChiaveEmail(patch),
    updated_at: serverTimestamp(),
  }));
}

// ============================================================================
// QUESTIONARI
// ============================================================================

/** Carica tutti i questionari, i più recenti per primi. */
export async function listQuestionnaires(): Promise<PlatformQuestionnaire[]> {
  const snap = await getDocs(collection(db, QUESTIONNAIRES_COLLECTION));
  const items = snap.docs.map((d) => mapSnap<PlatformQuestionnaire>(d));
  items.sort((a, b) => tsMillis(b.created_at) - tsMillis(a.created_at));
  return items;
}

/** Un questionario per id (null se non esiste). */
export async function getQuestionnaire(id: string): Promise<PlatformQuestionnaire | null> {
  const snap = await getDoc(doc(db, QUESTIONNAIRES_COLLECTION, id));
  if (!snap.exists()) return null;
  return { ...(snap.data() as Record<string, unknown>), id: snap.id } as PlatformQuestionnaire;
}

/**
 * Salva la definizione di un questionario dal costruttore.
 *
 * Il documento si riscrive INTERO (niente merge): con il merge una traduzione o un campo tolti
 * resterebbero sul disco, perché Firestore fonde le mappe annidate e ignora le chiavi assenti.
 * Si conservano i campi che il costruttore non conosce (spread del documento letto, regola
 * «mai whitelist») e quelli di creazione. Prima di scrivere si rifanno i controlli di
 * `problemiDefinizione`: un questionario difettoso non deve arrivare a chi compila.
 */
export async function saveQuestionnaire(
  input: QuestionarioSalvato,
  esistente?: PlatformQuestionnaire | null,
): Promise<string> {
  const problemi = problemiDefinizione(input);
  if (problemi.length > 0) {
    throw new Error(`Il questionario ha ${problemi.length} problemi: ${problemi[0].messaggio}`);
  }
  const uid = auth.currentUser?.uid ?? null;
  if (esistente) {
    const { id, ...campiLetti } = esistente;
    await setDoc(doc(db, QUESTIONNAIRES_COLLECTION, id), senzaUndefined({
      ...campiLetti,
      ...input,
      updated_at: serverTimestamp(),
      updated_by_uid: uid,
    }));
    return id;
  }
  const ref = doc(collection(db, QUESTIONNAIRES_COLLECTION));
  await setDoc(ref, senzaUndefined({
    ...input,
    created_at: serverTimestamp(),
    created_by_uid: uid,
  }));
  return ref.id;
}

/**
 * Quante risposte ha un questionario. Con almeno una risposta i codici di domande e opzioni non
 * si cambiano più: le statistiche contano i codici.
 */
export async function contaRisposteQuestionario(questionnaireId: string): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(db, RESPONSES_COLLECTION), where('questionnaire_id', '==', questionnaireId)),
  );
  return snap.data().count;
}

// ============================================================================
// LINK DI INVIO
// ============================================================================

/**
 * URL pubblico di compilazione per un token: la pagina del motore sul sito yumaos.com (dal
 * 18/10/2026; prima era nel gestionale, `app.yumaos.com/#/q/<token>`, che ora rimanda qui).
 * Lo stesso indirizzo che costruisce il server: un solo posto, `shared/questionari`.
 */
export function buildQuestionnairePublicUrl(token: string): string {
  return indirizzoPaginaPubblica('questionario', 'it', token);
}

export interface CreateQuestionnaireLinkInput {
  questionnaire_id: string;
  /** Presente = link personale pre-agganciato al lead; assente = link generico campagna. */
  platform_lead_id?: string;
  /** Partner di canale del link campagna (nome libero, max 80 char lato server). */
  partner_segnalatore?: string;
  /** Scadenza opzionale in ISO 8601. */
  scadenza?: string;
  /**
   * Nome leggibile del link (solo link generici): diventa il token, quindi l'indirizzo
   * (`lista-d-attesa-yumaos`). Forma `^[a-z0-9-]{16,64}$`; se esiste già il server risponde
   * `already-exists`.
   */
  token_leggibile?: string;
}

/** La forma che il server accetta per il nome leggibile di un link. */
export const TOKEN_LEGGIBILE_RE = /^[a-z0-9-]{16,64}$/;

export interface CreateQuestionnaireLinkResult {
  token: string;
  url: string;
}

/**
 * Genera un link questionario via callable (token generato server-side).
 * Se il server non restituisce l'URL completo lo deriva dal token.
 */
export async function createQuestionnaireLink(
  input: CreateQuestionnaireLinkInput,
): Promise<CreateQuestionnaireLinkResult> {
  const fn = httpsCallable(functions, 'createQuestionnaireLink');
  const result = await fn(stripUndefined({ ...input }));
  const data = result.data as { token?: string; url?: string };
  if (!data?.token) {
    throw new Error('risposta inattesa dalla generazione link (token mancante)');
  }
  return { token: data.token, url: data.url || buildQuestionnairePublicUrl(data.token) };
}

/** Elenca i link esistenti di un questionario, i più recenti per primi. */
export async function listLinksForQuestionnaire(
  questionnaireId: string,
): Promise<PlatformQuestionnaireLink[]> {
  const q = query(
    collection(db, LINKS_COLLECTION),
    where('questionnaire_id', '==', questionnaireId),
  );
  const snap = await getDocs(q);
  const links = snap.docs.map(
    (d) => ({ ...(d.data() as Record<string, unknown>), token: d.id }) as PlatformQuestionnaireLink,
  );
  links.sort((a, b) => tsMillis(b.created_at) - tsMillis(a.created_at));
  return links;
}

/** Disabilita un link (il flusso pubblico rifiuta i token disabled). */
export async function disableQuestionnaireLink(token: string): Promise<void> {
  await updateDoc(doc(db, LINKS_COLLECTION, token), { disabled: true });
}

// ============================================================================
// RISPOSTE
// ============================================================================

function sortResponses(items: PlatformQuestionnaireResponse[]): PlatformQuestionnaireResponse[] {
  items.sort((a, b) => tsMillis(b.completato_at) - tsMillis(a.completato_at));
  return items;
}

/** Risposte di un questionario, le più recenti per prime. */
export async function listResponsesForQuestionnaire(
  questionnaireId: string,
): Promise<PlatformQuestionnaireResponse[]> {
  const q = query(
    collection(db, RESPONSES_COLLECTION),
    where('questionnaire_id', '==', questionnaireId),
  );
  const snap = await getDocs(q);
  return sortResponses(snap.docs.map((d) => mapSnap<PlatformQuestionnaireResponse>(d)));
}

/** Risposte di una specifica azienda (per il dettaglio lead). */
export async function listResponsesForLead(
  platformLeadId: string,
): Promise<PlatformQuestionnaireResponse[]> {
  const q = query(
    collection(db, RESPONSES_COLLECTION),
    where('platform_lead_id', '==', platformLeadId),
  );
  const snap = await getDocs(q);
  return sortResponses(snap.docs.map((d) => mapSnap<PlatformQuestionnaireResponse>(d)));
}

// ============================================================================
// REGISTRO DELLE AZIONI (20/10/2026, ACCESSO-ANTICIPATO-MODEL.md § 3)
// ============================================================================

/**
 * Le voci del registro di un'azienda, dalla più recente. Le scrive solo il server (e la skill); la console le
 * legge e basta. Un errore di lettura risale: chi chiama lo mostra, non lo nasconde.
 */
export async function listRegistroLead(platformLeadId: string): Promise<VoceRegistroLead[]> {
  const snap = await getDocs(query(
    collection(db, LEADS_COLLECTION, platformLeadId, SOTTORACCOLTA_REGISTRO_LEAD),
    orderBy('quando', 'desc'),
  ));
  return snap.docs.map((d) => mapSnap<VoceRegistroLead>(d));
}

// ============================================================================
// SESSIONI (aperture della pagina pubblica)
// ============================================================================

/**
 * Le visite della pagina pubblica di un questionario. Le scrive solo il server; la lettura è del
 * super utente. Se le regole non la permettono ancora, l'errore risale e chi chiama lo dice.
 */
export async function listSessionsForQuestionnaire(
  questionnaireId: string,
): Promise<PlatformQuestionnaireSessione[]> {
  const q = query(
    collection(db, RACCOLTA_SESSIONI_QUESTIONARI),
    where('questionnaire_id', '==', questionnaireId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapSnap<PlatformQuestionnaireSessione>(d));
}

// ============================================================================
// LEAD PER ID (iscritti di un questionario)
// ============================================================================

/** Firestore accetta al massimo 30 valori in un filtro `in`. */
const MAX_IN = 30;

/** I lead con questi id, a gruppi di 30 (solo quelli che servono, non tutto il CRM). */
export async function getLeadsByIds(ids: string[]): Promise<Map<string, PlatformLead>> {
  const unici = Array.from(new Set(ids.filter(Boolean)));
  const out = new Map<string, PlatformLead>();
  for (let i = 0; i < unici.length; i += MAX_IN) {
    const gruppo = unici.slice(i, i + MAX_IN);
    const snap = await getDocs(query(collection(db, LEADS_COLLECTION), where(documentId(), 'in', gruppo)));
    snap.docs.forEach((d) => out.set(d.id, mapSnap<PlatformLead>(d)));
  }
  return out;
}

/**
 * «Non contattare» dalla console (la persona l'ha chiesto per telefono, per email…). Vince su
 * tutto: se c'era un consenso agli aggiornamenti si revoca, con la voce nello storico. Togliere
 * il segno non ridà il consenso: per quello serve una nuova iscrizione.
 */
export async function impostaNonContattare(lead: PlatformLead, nonContattare: boolean): Promise<void> {
  const patch: Record<string, unknown> = {
    non_contattare: nonContattare,
    non_contattare_at: nonContattare ? serverTimestamp() : null,
    updated_at: serverTimestamp(),
  };
  const consenso = lead.consenso_aggiornamenti;
  if (nonContattare && consenso && consenso.stato !== 'revocato') {
    const voce: VoceStoricoConsenso = { stato: 'revocato', il: new Date().toISOString(), come: 'console' };
    patch['consenso_aggiornamenti.stato'] = 'revocato';
    patch['consenso_aggiornamenti.revocato_at'] = serverTimestamp();
    patch['consenso_aggiornamenti.storico'] = arrayUnion(voce);
  }
  await updateDoc(doc(db, LEADS_COLLECTION, lead.id), patch);
}
